import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import InviteLinkList from '@/components/InviteLinkList';
import { useInviteLinks } from '@/hooks/useInviteLinks';
import { INVITE_LINK_PRESETS } from '@/constants';
import type { InviteLinkPreset } from '@/constants';
import { getApiErrorMessage, getInviteLinkErrorCode } from '@/utils/inviteLink';
import type { CreateInviteLinkData } from '@/types/inviteLink';
import styles from './styles.module.scss';

interface InviteLinkPanelProps {
  tournamentId: number;
}

// Bounds mirror the backend validator for the multi-use preset.
const MIN_MULTI_USES = 2;
const MIN_EXPIRY_DAYS = 1;
const MAX_EXPIRY_DAYS = 90;

const PRESET_OPTIONS: { value: InviteLinkPreset; labelKey: string; testId: string }[] = [
  { value: INVITE_LINK_PRESETS.ONE_TIME, labelKey: 'inviteLink.presetOneTime', testId: 'invite-link-preset-one-time' },
  { value: INVITE_LINK_PRESETS.MULTI_USE, labelKey: 'inviteLink.presetMultiUse', testId: 'invite-link-preset-multi-use' },
];

/** Parses an optional positive-integer input: '' → undefined, invalid → NaN. */
const parseOptionalInt = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN;
};

/**
 * "Link / QR" tab of the invite form: create one-time or multi-use invite links and manage
 * the tournament's existing links. Rendered only for users with `canInvite`.
 */
export const InviteLinkPanel: React.FC<InviteLinkPanelProps> = ({ tournamentId }) => {
  const { t } = useTranslation('tournament');
  const { linksQuery, createMutation, revokeMutation, deleteMutation } = useInviteLinks(tournamentId);

  const [preset, setPreset] = useState<InviteLinkPreset>(INVITE_LINK_PRESETS.ONE_TIME);
  const [maxUsesInput, setMaxUsesInput] = useState('');
  const [expiryInput, setExpiryInput] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const fieldIdPrefix = useId();
  const maxUsesErrorId = `${fieldIdPrefix}-max-uses-error`;
  const expiryErrorId = `${fieldIdPrefix}-expiry-error`;

  const isMultiUse = preset === INVITE_LINK_PRESETS.MULTI_USE;
  const maxUses = parseOptionalInt(maxUsesInput);
  const expiresInDays = parseOptionalInt(expiryInput);
  const maxUsesInvalid = maxUses !== undefined && (Number.isNaN(maxUses) || maxUses < MIN_MULTI_USES);
  const expiryInvalid = expiresInDays !== undefined
    && (Number.isNaN(expiresInDays) || expiresInDays < MIN_EXPIRY_DAYS || expiresInDays > MAX_EXPIRY_DAYS);
  const hasInputErrors = isMultiUse && (maxUsesInvalid || expiryInvalid);

  const describeError = (error: unknown): string => {
    const code = getInviteLinkErrorCode(error);
    if (code) return t(`inviteLink.errors.${code}`);
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 503) return t('inviteLink.errors.unavailable');
    if (status === 403) return t('noPermissionToInvite');
    return getApiErrorMessage(error) ?? t('inviteLink.errors.generic');
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasInputErrors || createMutation.isPending) return;

    const data: CreateInviteLinkData = { preset };
    if (isMultiUse) {
      if (maxUses !== undefined) data.maxUses = maxUses;
      if (expiresInDays !== undefined) data.expiresInDays = expiresInDays;
    }

    createMutation.mutate(data, {
      onSuccess: (created) => {
        toast.success(t('inviteLink.created'));
        setExpandedId(created.id);
        setMaxUsesInput('');
        setExpiryInput('');
      },
      onError: (error) => toast.error(describeError(error)),
    });
  };

  const handleRevoke = (linkId: number) => {
    revokeMutation.mutate(linkId, {
      onSuccess: () => {
        toast.success(t('inviteLink.revoked'));
        setExpandedId((prev) => (prev === linkId ? null : prev));
      },
      onError: (error) => toast.error(describeError(error)),
    });
  };

  const handleDelete = (linkId: number) => {
    deleteMutation.mutate(linkId, {
      onSuccess: () => {
        toast.success(t('inviteLink.deleted'));
        setExpandedId((prev) => (prev === linkId ? null : prev));
      },
      onError: (error) => toast.error(describeError(error)),
    });
  };

  const links = linksQuery.data ?? [];

  return (
    <div className={styles.panel} data-testid="invite-link-panel">
      <form className={styles.createForm} onSubmit={handleCreate} data-testid="invite-link-create-form">
        <div className={styles.presets} role="group" aria-label={t('inviteLink.presetLabel')}>
          {PRESET_OPTIONS.map(({ value, labelKey, testId }) => (
            <button
              key={value}
              type="button"
              className={`btn btn--sm ${styles.preset}`}
              aria-pressed={preset === value}
              onClick={() => setPreset(value)}
              data-testid={testId}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        <p className={styles.hint}>
          {isMultiUse ? t('inviteLink.presetMultiUseHint') : t('inviteLink.presetOneTimeHint')}
        </p>

        {isMultiUse && (
          <div className={styles.fields}>
            <label className={styles.field}>
              <span className={styles.label}>{t('inviteLink.maxUsesLabel')}</span>
              <input
                className={`field ${maxUsesInvalid ? 'field--error' : ''}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={maxUsesInput}
                onChange={(e) => setMaxUsesInput(e.target.value)}
                placeholder={t('inviteLink.maxUsesPlaceholder')}
                aria-invalid={maxUsesInvalid}
                aria-describedby={maxUsesInvalid ? maxUsesErrorId : undefined}
                data-testid="invite-link-max-uses"
              />
              {maxUsesInvalid && (
                <span id={maxUsesErrorId} className={styles.error}>{t('inviteLink.maxUsesInvalid', { min: MIN_MULTI_USES })}</span>
              )}
            </label>
            <label className={styles.field}>
              <span className={styles.label}>{t('inviteLink.expiresInDaysLabel')}</span>
              <input
                className={`field ${expiryInvalid ? 'field--error' : ''}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={expiryInput}
                onChange={(e) => setExpiryInput(e.target.value)}
                placeholder={t('inviteLink.expiresInDaysPlaceholder')}
                aria-invalid={expiryInvalid}
                aria-describedby={expiryInvalid ? expiryErrorId : undefined}
                data-testid="invite-link-expires-days"
              />
              {expiryInvalid && (
                <span id={expiryErrorId} className={styles.error}>
                  {t('inviteLink.expiresInDaysInvalid', { min: MIN_EXPIRY_DAYS, max: MAX_EXPIRY_DAYS })}
                </span>
              )}
            </label>
          </div>
        )}

        <div className={styles.createActions}>
          <button
            type="submit"
            className="btn btn--sm"
            disabled={hasInputErrors || createMutation.isPending}
            data-testid="invite-link-create"
          >
            {createMutation.isPending ? t('inviteLink.creating') : t('inviteLink.create')}
          </button>
        </div>
      </form>

      <section className={styles.listSection} aria-labelledby="invite-link-list-title">
        <h3 id="invite-link-list-title" className={styles.listTitle}>{t('inviteLink.listTitle')}</h3>
        {linksQuery.isLoading && (
          <div
            className={styles.status}
            role="status"
            aria-live="polite"
            aria-label={t('inviteLink.loadingList')}
            data-testid="invite-link-list-loading"
          >
            <span className="spinner spinner--sm" aria-hidden="true" />
          </div>
        )}
        {linksQuery.isError && (
          <p className={styles.status} data-testid="invite-link-list-error">{describeError(linksQuery.error)}</p>
        )}
        {linksQuery.isSuccess && links.length === 0 && (
          <p className={styles.status} data-testid="invite-link-list-empty">{t('inviteLink.empty')}</p>
        )}
        {links.length > 0 && (
          <InviteLinkList
            links={links}
            expandedId={expandedId}
            onToggleExpand={(id) => setExpandedId((prev) => (prev === id ? null : id))}
            onRevoke={handleRevoke}
            revokingId={revokeMutation.isPending ? revokeMutation.variables ?? null : null}
            onDelete={handleDelete}
            deletingId={deleteMutation.isPending ? deleteMutation.variables ?? null : null}
          />
        )}
      </section>
    </div>
  );
};

export default InviteLinkPanel;
